import React, { useState } from 'react';
import { Button, Tooltip } from 'antd';
import { RobotOutlined, ThunderboltOutlined } from '@ant-design/icons';
import AIInfoModal from './AIInfoModal';

const AIInfoButton = ({ 
    menuItem, 
    size = 'default', 
    type = 'default',
    icon,
    children,
    tooltip = "Get AI-powered information about this dish",
    showCustomChat = true,
    variant = 'default', // 'default', 'gradient', 'outlined'
    ...props 
}) => {
    const [modalVisible, setModalVisible] = useState(false);

    const handleClick = () => {
        setModalVisible(true);
    };

    const handleClose = () => {
        setModalVisible(false);
    };

    // Default icon with sparkle effect
    const defaultIcon = icon || <ThunderboltOutlined className="ai-icon-sparkle" />;

    // Get button styles based on variant
    const getButtonStyles = () => {
        const baseStyles = {
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            fontWeight: '500',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px'
        };

        if (variant === 'gradient') {
            return {
                ...baseStyles,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                color: 'white',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
            };
        } else if (variant === 'outlined') {
            return {
                ...baseStyles,
                background: 'transparent',
                border: '1.5px solid #C97D60',
                color: '#C97D60',
                boxShadow: '0 2px 8px rgba(201, 125, 96, 0.15)',
            };
        } else {
            // default variant
            return {
                ...baseStyles,
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                border: '1.5px solid rgba(102, 126, 234, 0.3)',
                color: '#667eea',
                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.2)',
            };
        }
    };

    const buttonStyles = getButtonStyles();
    const hoverStyles = {
        transform: 'translateY(-2px)',
        boxShadow: variant === 'gradient' 
            ? '0 6px 20px rgba(102, 126, 234, 0.5)' 
            : variant === 'outlined'
            ? '0 4px 12px rgba(201, 125, 96, 0.3)'
            : '0 4px 16px rgba(102, 126, 234, 0.3)',
    };

    return (
        <>
            <style>{`
                .ai-button-wrapper {
                    position: relative;
                }
                .ai-button-wrapper::before {
                    content: '';
                    position: absolute;
                    top: -50%;
                    left: -50%;
                    width: 200%;
                    height: 200%;
                    background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.3), transparent);
                    transform: rotate(45deg);
                    transition: all 0.6s;
                    opacity: 0;
                }
                .ai-button-wrapper:hover::before {
                    animation: shine 0.6s ease-in-out;
                }
                @keyframes shine {
                    0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); opacity: 0; }
                    50% { opacity: 1; }
                    100% { transform: translateX(100%) translateY(100%) rotate(45deg); opacity: 0; }
                }
                .ai-icon-sparkle {
                    animation: sparkle 2s ease-in-out infinite;
                }
                @keyframes sparkle {
                    0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
                    50% { transform: scale(1.1) rotate(180deg); opacity: 0.8; }
                }
            `}</style>
            <Tooltip title={tooltip} placement="top">
                <div className="ai-button-wrapper" style={{ display: 'inline-block' }}>
                    <Button
                        type={type === 'default' ? 'default' : type}
                        size={size}
                        icon={defaultIcon}
                        onClick={handleClick}
                        onMouseEnter={(e) => {
                            Object.assign(e.currentTarget.style, hoverStyles);
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = '';
                            e.currentTarget.style.boxShadow = buttonStyles.boxShadow;
                        }}
                        style={buttonStyles}
                        {...props}
                    >
                        {children || 'Ask AI'}
                    </Button>
                </div>
            </Tooltip>
            
            <AIInfoModal
                visible={modalVisible}
                onClose={handleClose}
                menuItem={menuItem}
                showCustomChat={showCustomChat}
            />
        </>
    );
};

export default AIInfoButton;
